// API §6.3's upsert rule — what staging a value DOES to the open change-set (REQ-101 AC3, ENGINE §11).
//
// It lives in a PUBLIC module rather than inside the engine for the reason D-176 gave for `@sl/core/shares`:
// two places need the same rule and there must be exactly one implementation. The server applies it to decide
// what to store (`demo/server/changeSetStore.ts`, and the C# Sandbox through the same golden suite); the apps
// apply it to decide what to paint before the server answers (`@sl/core/queries` `optimisticStage`). If those
// two ever disagreed about whether an edit adds, merges, removes or does nothing, the optimistic marker would
// flicker into a different answer 120 ms later — which is precisely the defect an optimistic update exists to
// avoid. `src/engine/changeSets.ts` imports and re-exports both names, so the engine's own callers and the
// golden vectors are unmoved.
//
// It needs no rules table and decides no money: the only judgement in it is what "the same value" means, and
// that is the contract's own 6-decimal scale (API §1.4, `@sl/core/num` `units`).

import { units } from '../num/exact';
import type { ChangeField, ChangeValue, StageChangeRequest } from '../types/changes';
import type { Money } from '../types/primitives';

export interface StagedItemLike {
  entityId: string;
  field: ChangeField;
  /** The live value when the item was first staged; a merge never moves it (REQ-101 AC1's struck-through). */
  from: ChangeValue;
  to: ChangeValue;
}

export type MergeOutcome = 'added' | 'merged' | 'removed' | 'not_staged';

export interface MergeResult {
  outcome: MergeOutcome;
  /** The new list, in the old order; an `added` item goes last, a `removed` one leaves no gap. */
  items: StagedItemLike[];
}

/** Two `ChangeValue`s denote the same value — REQ-101 AC3's "setting it back to the original". */
export function sameChangeValue(a: ChangeValue | null, b: ChangeValue | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'status') return a.isOn === (b as { isOn: boolean }).isOn;
  const other = b as { amount: Money };
  if (!Number.isFinite(a.amount) || !Number.isFinite(other.amount))
    return Object.is(a.amount, other.amount);
  return units(a.amount) === units(other.amount);
}

/**
 * ENGINE §11 / API §6.3: upsert on `(entityId, field)` — editing the same field twice updates the one item and
 * keeps its original `from`, and a `to` equal to the LIVE value removes the item instead of staging a no-op
 * (REQ-101 AC3). `not_staged` is the fourth answer: back-to-original when nothing was staged there is not an
 * error and not a change — it is nothing at all.
 */
export function mergeItem(
  items: readonly StagedItemLike[],
  request: StageChangeRequest,
  live: ChangeValue,
): MergeResult {
  const next = items.map((it) => ({ ...it }));
  const at = next.findIndex((it) => it.entityId === request.entityId && it.field === request.field);
  if (sameChangeValue(request.to, live)) {
    if (at === -1) return { outcome: 'not_staged', items: next };
    next.splice(at, 1);
    return { outcome: 'removed', items: next };
  }
  if (at !== -1) {
    next[at] = { ...next[at]!, to: request.to };
    return { outcome: 'merged', items: next };
  }
  next.push({ entityId: request.entityId, field: request.field, from: live, to: request.to });
  return { outcome: 'added', items: next };
}
